/**
 * Bottle list component with table/card views
 */

import { Table } from '../common';
import BottleCard from './BottleCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { formatCurrency, formatProof, formatAge, formatDate } from '../../utils/formatters';
import './BottleList.css';

/**
 * Generates column definitions with action handlers
 * @param {Function} onViewImages - View images handler
 * @param {Function} onEdit - Edit handler
 * @param {Function} onDelete - Delete handler
 * @returns {Array} Column definitions
 */
function getColumns(onViewImages, onEdit, onDelete) {
  return [
    {
      key: 'bottle_name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <span className="bottle-list__name-cell">{value}</span>
      )
    },
    {
      key: 'distilleries',
      label: 'Distillery',
      sortable: false,
      render: (value) => value?.distillery_name || '-'
    },
    { key: 'type', label: 'Type', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`table__status table__status--${value.toLowerCase()}`}>
          {value}
        </span>
      )
    },
    {
      key: 'proof',
      label: 'Proof',
      sortable: true,
      render: (value) => formatProof(value)
    },
    {
      key: 'age_months',
      label: 'Age',
      sortable: true,
      render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{formatAge(value)}</span>
    },
    {
      key: 'purchase_date',
      label: 'Purchased',
      sortable: true,
      render: (value) => formatDate(value)
    },
    {
      key: 'purchase_price',
      label: 'Price',
      sortable: true,
      render: (value) => formatCurrency(value)
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '120px',
      render: (value, row) => (
        <div className="table__actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="table__icon-btn"
            onClick={() => onViewImages(row)}
            title="View Images"
            aria-label="View bottle images"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <button
            className="table__icon-btn"
            onClick={() => onEdit(row)}
            title="Edit"
            aria-label="Edit bottle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            className="table__icon-btn table__icon-btn--danger"
            onClick={() => onDelete(row)}
            title="Delete"
            aria-label="Delete bottle"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      )
    }
  ];
}

/**
 * List of bottles with responsive table/card layout
 * @param {Object} props - Component props
 * @param {Array} props.bottles - Bottle data
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort direction
 * @param {Function} props.onSort - Sort change handler
 * @param {Function} props.onSelect - Row/card selection handler
 * @param {Function} props.onViewImages - View images handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} List element
 */
export default function BottleList({
  bottles,
  sortBy,
  sortOrder,
  onSort,
  onSelect,
  onViewImages,
  onEdit,
  onDelete,
  loading
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div className="bottle-card-list">
        {loading ? (
          <div className="bottle-card-list__loading">Loading...</div>
        ) : bottles.length === 0 ? (
          <div className="bottle-card-list__empty">No bottles found</div>
        ) : (
          bottles.map(bottle => (
            <BottleCard
              key={bottle.id}
              bottle={bottle}
              onClick={() => onSelect(bottle)}
              onViewImages={() => onViewImages(bottle)}
              onEdit={() => onEdit(bottle)}
              onDelete={() => onDelete(bottle)}
            />
          ))
        )}
      </div>
    );
  }

  const columns = getColumns(onViewImages, onEdit, onDelete);

  return (
    <Table
      columns={columns}
      data={bottles}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      onRowClick={onSelect}
      loading={loading}
      emptyMessage="No bottles found"
    />
  );
}
